import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import * as Router from "react-router-dom";
import * as paths from "routes/paths";
import { Market } from "model/market";

interface Props {
	open: boolean;
	markets: string[];
	allMarkets: Market[];
	maxMarkets: number;
	onSave: (markets: string[]) => void;
	onCancel: () => void;
}

interface State {
	selected: string[];
	search: string;
}

class Component extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			selected: props.markets.filter(id => id && id !== ""),
			search: "",
		};
	}

	public componentDidUpdate(prevProps: Props) {
		if (!prevProps.open && this.props.open) {
			this.setState({
				selected: this.props.markets.filter(id => id && id !== ""),
				search: "",
			});
		}
	}

	private handleSave = () => {
		const { maxMarkets, onSave } = this.props;
		const { selected } = this.state;
		const padded = Array.from({ length: maxMarkets }, (_, i) => selected[i] ?? "");
		onSave(padded);
	};

	public render() {
		const { open, allMarkets, maxMarkets, onCancel } = this.props;
		const { selected, search } = this.state;
		const atMax = selected.length >= maxMarkets;

		const filtered = search
			? allMarkets.filter(m =>
				m.name.toLowerCase().includes(search.toLowerCase()) &&
				!selected.includes(m.id.toString())
			)
			: [];

		return (
			<Mui.Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
				<Mui.DialogTitle>
					<Mui.Box display="flex" alignItems="center" justifyContent="space-between">
						<Mui.Typography variant="h6" style={{ fontWeight: "bold", fontSize: 24 }}>Edit Markets</Mui.Typography>
						<Mui.IconButton size="small" onClick={onCancel}><Icons.X size={24} /></Mui.IconButton>
					</Mui.Box>
				</Mui.DialogTitle>
				<Mui.Divider style={{ marginLeft: 24, marginRight: 24, backgroundColor: "#EBEBEB" }} />
				<Mui.DialogContent>
					<Mui.Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
						Add up to {maxMarkets} markets to display in your search dropdown.
					</Mui.Typography>
					{allMarkets.length === 0 ? (
						<Mui.Typography variant="body2" color="textSecondary">
							You don't have any markets yet.{" "}
							<Router.Link to={paths.markets}>Click here to add some.</Router.Link>
						</Mui.Typography>
					) : (
						<>
							<Mui.TextField
								fullWidth
								variant="outlined"
								size="small"
								placeholder="Search Markets"
								value={search}
								onChange={(e) => this.setState({ search: e.target.value })}
								disabled={atMax}
								InputProps={{
									startAdornment: (
										<Mui.InputAdornment position="start">
											<Icons.Search size={16} />
										</Mui.InputAdornment>
									),
								}}
							/>
							{filtered.length > 0 && (
								<Mui.Paper variant="outlined" style={{ marginTop: 4, maxHeight: 160, overflowY: "auto" }}>
									<Mui.List dense disablePadding>
										{filtered.map(market => (
											<Mui.ListItem
												key={market.id}
												button
												onClick={() => {
													this.setState(prev => ({
														selected: [...prev.selected, market.id.toString()],
														search: "",
													}));
												}}
											>
												<Mui.ListItemText primary={market.name} />
											</Mui.ListItem>
										))}
									</Mui.List>
								</Mui.Paper>
							)}
							<Mui.Typography variant="body2" color="textSecondary" style={{ marginTop: 16, marginBottom: 8 }}>
								{selected.length} / {maxMarkets} Added
							</Mui.Typography>
							<Mui.Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
								{selected.map(id => {
									const market = allMarkets.find(m => m.id.toString() === id);
									if (!market) return null;
									return (
										<Mui.Chip
											key={id}
											label={market.name}
											size="small"
											style={{ backgroundColor: "#EBEBEB", color: "#1C1B1F" }}
											deleteIcon={<Icons.X size={14} style={{ color: "#1C1B1F" }} />}
											onDelete={() => this.setState(prev => ({
												selected: prev.selected.filter(s => s !== id),
											}))}
										/>
									);
								})}
							</Mui.Box>
						</>
					)}
				</Mui.DialogContent>
				<Mui.DialogActions style={{ padding: "16px 24px" }}>
					<Mui.Button variant="outlined" color="primary" onClick={onCancel}>Cancel</Mui.Button>
					<Mui.Button variant="contained" color="primary" onClick={this.handleSave}>Apply</Mui.Button>
				</Mui.DialogActions>
			</Mui.Dialog>
		);
	}
}

export const MarketsModal = Component;
